import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  DollarSign, TrendingUp, TrendingDown, Clock, Plus, Trash2, Edit2, X, 
  Calendar, Users, FileText, Download, Filter, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_TYPES = [
  { value: 'plan_payment', label: 'Plan Payment' },
  { value: 'package_payment', label: 'Package Payment' },
  { value: 'addon_session', label: 'Add-on Session' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_STATUSES = [
  { value: 'received', label: 'Received', color: 'bg-green-500/20 text-green-400' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/20 text-red-400' },
];

const CoachFinancials = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [payments, setPayments] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [pendingData, setPendingData] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    athlete_id: '',
    amount: '',
    payment_type: 'plan_payment',
    description: '',
    payment_date: new Date().toISOString().split('T')[0],
    status: 'received'
  });

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const [summaryRes, reportRes, paymentsRes, athletesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/financial/summary?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/financial/report?start_date=${dateRange.start}&end_date=${dateRange.end}&group_by=${groupBy}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/payments?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/athletes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/financial/pending`, { headers: getAuthHeaders() })
      ]);
      
      setSummary(summaryRes.data);
      setReport(reportRes.data);
      setPayments(paymentsRes.data);
      setAthletes(athletesRes.data);
      setPendingData(pendingRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Payment updated');
      } else {
        await axios.post(`${API}/payments`, payload, { headers: getAuthHeaders() });
        toast.success('Payment recorded');
      }
      closeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment');
    }
    setSubmitting(false);
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      athlete_id: payment.athlete_id,
      amount: payment.amount.toString(),
      payment_type: payment.payment_type,
      description: payment.description || '',
      payment_date: payment.payment_date.split('T')[0],
      status: payment.status
    });
    setShowForm(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`, { headers: getAuthHeaders() });
      toast.success('Payment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPayment(null);
    setFormData({
      athlete_id: '',
      amount: '',
      payment_type: 'plan_payment',
      description: '',
      payment_date: new Date().toISOString().split('T')[0],
      status: 'received'
    });
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    const endDate = new Date().toISOString().split('T')[0];
    let startDate;
    
    switch (range) {
      case 'today':
        startDate = endDate;
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'quarter':
        startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1).toISOString().split('T')[0];
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    }
    
    setDateRange({ start: startDate, end: endDate });
  };

  const filteredPayments = filterStatus 
    ? payments.filter(p => p.status === filterStatus)
    : payments;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'pending', label: 'Pending', icon: Clock },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-financials">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Financial Management</h1>
          <p className="text-muted-foreground">Track your revenue and payments</p>
        </div>
        <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Record Payment
        </Button>
      </div>

      {/* Date Range Filters */}
      <div className="bg-card border border-border/40 p-4 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-2">
            {['week', 'month', 'quarter', 'year'].map((range) => (
              <Button
                key={range}
                variant="outline"
                size="sm"
                onClick={() => setQuickDateRange(range)}
                className="capitalize"
              >
                {range}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-40"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-primary text-black' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">
                {editingPayment ? 'Edit Payment' : 'Record Payment'}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Athlete</label>
                <select
                  value={formData.athlete_id}
                  onChange={(e) => setFormData({...formData, athlete_id: e.target.value})}
                  required
                  className="w-full p-3 bg-transparent border border-border rounded-sm"
                >
                  <option value="" className="bg-card">Select Athlete</option>
                  {athletes.map(a => (
                    <option key={a.id} value={a.id} className="bg-card">{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Date</label>
                  <Input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Type</label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    {PAYMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value} className="bg-card">{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    {PAYMENT_STATUSES.map(status => (
                      <option key={status.value} value={status.value} className="bg-card">{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Description (optional)</label>
                <Input
                  placeholder="Payment notes..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                  {submitting ? 'Saving...' : editingPayment ? 'Update' : 'Record'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-card border border-border/40 rounded-sm p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && summary && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-muted/30 p-5 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="text-blue-400" size={20} />
                  <span className="text-xs text-muted-foreground">Total Sold</span>
                </div>
                <p className="text-3xl font-bold">${summary.total_sold.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.athletes_count} athletes</p>
              </div>
              
              <div className="bg-green-500/10 border border-green-500/30 p-5 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <ArrowUpRight className="text-green-400" size={20} />
                  <span className="text-xs text-green-400">Received</span>
                </div>
                <p className="text-3xl font-bold text-green-400">${summary.total_received.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.payments_by_status?.received || 0} payments</p>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-5 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="text-yellow-400" size={20} />
                  <span className="text-xs text-yellow-400">Pending</span>
                </div>
                <p className="text-3xl font-bold text-yellow-400">${summary.total_pending.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.payments_by_status?.pending || 0} pending</p>
              </div>
              
              <div className="bg-muted/30 p-5 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="text-primary" size={20} />
                  <span className="text-xs text-muted-foreground">Collection Rate</span>
                </div>
                <p className="text-3xl font-bold">
                  {summary.total_sold > 0 
                    ? Math.round((summary.total_received / summary.total_sold) * 100) 
                    : 0}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">of total sold</p>
              </div>
            </div>

            {/* Recent Payments */}
            <div>
              <h3 className="font-heading text-lg uppercase mb-4">Recent Payments</h3>
              {payments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payments recorded in this period</p>
              ) : (
                <div className="space-y-2">
                  {payments.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                      <div>
                        <p className="font-medium">{payment.athlete_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(() => {
                            const dateStr = payment.payment_date || payment.due_date;
                            if (!dateStr) return 'No date';
                            const date = new Date(dateStr);
                            return isNaN(date.getTime()) ? 'No date' : date.toLocaleDateString();
                          })()} • {PAYMENT_TYPES.find(t => t.value === payment.payment_type)?.label || payment.payment_type}
                          {!payment.payment_date && payment.due_date && <span className="text-primary ml-1">(due)</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg">${payment.amount.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${PAYMENT_STATUSES.find(s => s.value === payment.status)?.color}`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Athletes */}
            {report?.top_athletes?.length > 0 && (
              <div>
                <h3 className="font-heading text-lg uppercase mb-4">Top Athletes by Revenue</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {report.top_athletes.slice(0, 5).map((athlete, idx) => (
                    <div key={idx} className="bg-muted/30 p-3 rounded-sm text-center">
                      <p className="font-medium text-sm truncate">{athlete.name}</p>
                      <p className="text-primary font-bold">${athlete.total.toFixed(0)}</p>
                      <p className="text-xs text-muted-foreground">{athlete.count} payments</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg uppercase">Payment History</h3>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="p-2 bg-transparent border border-border rounded-sm text-sm"
              >
                <option value="" className="bg-card">All Status</option>
                {PAYMENT_STATUSES.map(s => (
                  <option key={s.value} value={s.value} className="bg-card">{s.label}</option>
                ))}
              </select>
            </div>

            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No payments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left p-3 text-xs uppercase">Date</th>
                      <th className="text-left p-3 text-xs uppercase">Due Date</th>
                      <th className="text-left p-3 text-xs uppercase">Athlete</th>
                      <th className="text-left p-3 text-xs uppercase">Type</th>
                      <th className="text-left p-3 text-xs uppercase">Amount</th>
                      <th className="text-left p-3 text-xs uppercase">Status</th>
                      <th className="text-right p-3 text-xs uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {filteredPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-muted/20">
                        <td className="p-3 text-sm">
                          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-sm">
                          {payment.due_date ? (
                            <span className={payment.status === 'pending' ? 'text-primary' : ''}>
                              {new Date(payment.due_date).toLocaleDateString()}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-3 text-sm">{payment.athlete_name}</td>
                        <td className="p-3 text-sm">{PAYMENT_TYPES.find(t => t.value === payment.payment_type)?.label || payment.payment_type}</td>
                        <td className="p-3 font-semibold">${payment.amount.toFixed(2)}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 rounded ${PAYMENT_STATUSES.find(s => s.value === payment.status)?.color}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <button onClick={() => handleEdit(payment)} className="p-1 hover:bg-muted rounded-sm">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(payment.id)} className="p-1 hover:bg-muted rounded-sm text-red-400 ml-1">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pending Tab */}
        {activeTab === 'pending' && pendingData && (
          <div className="space-y-6">
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-sm">
              <p className="text-lg font-semibold text-yellow-400">
                Total Pending: ${pendingData.total_pending_amount?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-muted-foreground">
                {pendingData.athletes_with_pending?.length || 0} athletes with pending payments
              </p>
            </div>

            <h3 className="font-heading text-lg uppercase">Athletes with Pending Payments</h3>
            {pendingData.athletes_with_pending?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No pending payments! 🎉</p>
            ) : (
              <div className="space-y-3">
                {pendingData.athletes_with_pending?.map((athlete) => (
                  <div key={athlete.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
                    <div>
                      <p className="font-medium">{athlete.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {athlete.sport} • {athlete.position}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${athlete.plan_value?.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        athlete.payment_status === 'overdue' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {athlete.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && report && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg uppercase">Revenue by Period</h3>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="p-2 bg-transparent border border-border rounded-sm text-sm"
              >
                <option value="day" className="bg-card">Daily</option>
                <option value="week" className="bg-card">Weekly</option>
                <option value="month" className="bg-card">Monthly</option>
              </select>
            </div>

            {/* Simple Bar Chart */}
            {report.data?.length > 0 ? (
              <div className="space-y-2">
                {report.data.map((item, idx) => {
                  const maxValue = Math.max(...report.data.map(d => d.total));
                  const width = maxValue > 0 ? (item.total / maxValue) * 100 : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="w-24 text-sm text-muted-foreground">{item.period}</span>
                      <div className="flex-1 bg-muted/30 rounded-sm h-8 relative overflow-hidden">
                        <div 
                          className="absolute inset-y-0 left-0 bg-primary/80 rounded-sm transition-all"
                          style={{ width: `${width}%` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-medium">
                          ${item.total.toFixed(0)}
                        </span>
                      </div>
                      <span className="w-16 text-xs text-muted-foreground text-right">
                        {item.count} pmts
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No data for selected period</p>
            )}

            {/* Payment Type Breakdown */}
            {report.payment_types && Object.keys(report.payment_types).length > 0 && (
              <div>
                <h4 className="font-heading text-md uppercase mb-3">By Payment Type</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(report.payment_types).map(([type, data]) => (
                    <div key={type} className="bg-muted/30 p-3 rounded-sm">
                      <p className="text-xs text-muted-foreground uppercase">{type.replace('_', ' ')}</p>
                      <p className="text-lg font-bold text-primary">${data.total.toFixed(0)}</p>
                      <p className="text-xs text-muted-foreground">{data.count} payments</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="bg-muted/30 p-4 rounded-sm">
              <h4 className="font-heading text-md uppercase mb-3">Period Totals</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-green-400">${report.totals?.received?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Received</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">${report.totals?.pending?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">${report.totals?.cancelled?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Cancelled</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{report.totals?.total_payments || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Payments</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachFinancials;
